import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Copy, Link2, Loader2, Share2, ShieldX } from "lucide-react";
import {
  createShareLink,
  listShareLinks,
  revokeShareLink,
  buildShareUrl,
} from "@/lib/projects";

export default function ShareDialog({
  open,
  onOpenChange,
  analysisId,
  projectName,
}) {
  const [ttl, setTtl] = useState(7);
  const [links, setLinks] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!analysisId) return;
    try {
      const list = await listShareLinks(analysisId);
      setLinks(list || []);
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Impossible de charger les liens."
      );
    }
  }, [analysisId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const active = links.find((l) => !l.revoked);

  const generate = async () => {
    setBusy(true);
    try {
      const link = await createShareLink(analysisId, ttl);
      toast.success("Lien sécurisé généré.");
      setLinks((prev) => [link, ...prev]);
      // Copy to clipboard
      const url = buildShareUrl(link.token);
      try {
        await navigator.clipboard.writeText(url);
        toast.info("Lien copié dans le presse-papiers.");
      } catch {
        /* clipboard blocked — still visible in UI */
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Génération impossible.");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (token) => {
    const url = buildShareUrl(token);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Lien copié.");
    } catch {
      toast.error("Impossible de copier automatiquement. Sélectionnez le lien.");
    }
  };

  const revoke = async (id) => {
    setBusy(true);
    try {
      await revokeShareLink(id);
      toast.success("Lien révoqué.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Révocation impossible.");
    } finally {
      setBusy(false);
    }
  };

  const formatExpiry = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("fr-FR");
    } catch {
      return iso;
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        className="sm:max-w-xl"
        data-testid="share-dialog"
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-brand-blue dark:text-brand-gold" />
            Partager l'analyse
          </AlertDialogTitle>
          <AlertDialogDescription>
            Générez un lien public <strong>en lecture seule</strong> pour
            partager l'analyse de « {projectName} ». Un seul lien actif par
            analyse. Les liens générés précédemment sont automatiquement
            révoqués.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4">
          <div
            className="rounded-md border border-dashed border-border bg-muted/30 p-3"
            data-testid="share-ttl-row"
          >
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">
              Expiration :{" "}
              <span className="font-mono font-bold text-foreground">
                {ttl} jours
              </span>
            </Label>
            <Slider
              min={1}
              max={30}
              step={1}
              value={[ttl]}
              onValueChange={(v) => setTtl(Number(v[0]))}
              className="mt-2"
              data-testid="share-ttl-slider"
            />
          </div>

          {active && (
            <div
              className="rounded-md border border-emerald-400/40 bg-emerald-50 p-3 dark:bg-emerald-950/30"
              data-testid="share-active-link"
            >
              <div className="flex items-center justify-between gap-2">
                <Badge
                  variant="outline"
                  className="border-emerald-400 text-emerald-800 dark:text-emerald-200"
                >
                  Actif · expire le {formatExpiry(active.expires_at)}
                </Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => revoke(active.id)}
                  disabled={busy}
                  data-testid="share-revoke-btn"
                >
                  <ShieldX className="mr-1 h-4 w-4" />
                  Révoquer
                </Button>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <Input
                  readOnly
                  value={buildShareUrl(active.token)}
                  className="font-mono text-xs"
                  data-testid="share-url-input"
                  onFocus={(e) => e.target.select()}
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copy(active.token)}
                  data-testid="share-copy-btn"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          <Button
            onClick={generate}
            disabled={busy}
            className="w-full"
            data-testid="share-generate-btn"
          >
            {busy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Génération…
              </>
            ) : (
              <>
                <Link2 className="mr-2 h-4 w-4" />
                {active
                  ? "Générer un nouveau lien (révoque l'actuel)"
                  : "Générer un lien sécurisé"}
              </>
            )}
          </Button>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="share-close-btn">
            Fermer
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
